import { produce } from "immer";
import { type FC, useCallback, useMemo } from "react";
import { useEventCallback } from "usehooks-ts";
import { useDialog } from "../common/dialog/Dialog";
import type { UseFormReturn } from "../common/form/useForm";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { Button } from "../components/Button";
import { ButtonIcon } from "../components/ButtonIcon";
import { Tag } from "../components/Tag";
import { useGoogleMyBusinessCategories } from "../settings/useIntegrations";
import { CategoryMappingPanel } from "./CategoryMappingPanel";
import { DialogAddService } from "./DialogAddService";
import type { FormMyBusinessLocationFields } from "./FormBusinessLocationFields";
import styles from "./FormBusinessServicesProducts.module.css";
import { VirtualizedSelectTmp } from "./VirtualisedSelectTmp";

type Props = {
	locationID: string;
	form: UseFormReturn<FormMyBusinessLocationFields>;
};

const FormBusinessServicesProducts: FC<Readonly<Props>> = ({
	form,
	locationID,
}) => {
	const categories = useGoogleMyBusinessCategories();
	const dialog = useDialog();

	const allSelectedCategories = useMemo(() => {
		return [
			form.values?.category,
			...(form.values?.additionalCategories ?? []),
		].filter((cat): cat is string => !!cat?.trim());
	}, [form.values.category, form.values.additionalCategories]);

	const selectedCategories = useMemo(() => {
		return allSelectedCategories.map((name) => {
			const found = categories.find((x) => x.name === name);
			if (found) {
				return found;
			}
			// Category is on the form but missing from the cached Google list —
			// still offer it for free-text services.
			const label = name
				.replace(/^categories\//i, "")
				.replace(/^gcid[_:]/i, "")
				.replace(/_/g, " ");
			return {
				name,
				displayName: label.charAt(0).toUpperCase() + label.slice(1),
				serviceTypes: [],
			};
		});
	}, [categories, allSelectedCategories]);

	const allServices = useMemo(() => {
		return categories
			.flatMap((x) => x.serviceTypes)
			.filter((x) => x !== undefined);
	}, [categories]);

	const handleAdditionalCategoryChange = useEventCallback(
		(newValue: string | null | undefined, idx: number) => {
			const additionalValues = produce(
				form.values.additionalCategories ?? [],
				(draft) => {
					draft[idx] = newValue ?? "";
				},
			);

			form.setFieldValue("additionalCategories", additionalValues, true);
		},
	);

	const canAddNewCategory = !form.values.additionalCategories?.includes("");

	const handleAddCategory = useEventCallback(() => {
		form.setFieldValue("additionalCategories", [
			...(form.values.additionalCategories || []),
			"",
		]);
	});

	const handleRemoveCategory = useEventCallback((index: number) => {
		const categories = [...(form.values.additionalCategories || [])];
		categories.splice(index, 1);
		form.setFieldValue("additionalCategories", categories);
	});

	const handleRemoveStructuredService = useEventCallback((id: string) => {
		const services = form.values.structuredServices?.filter(
			(srv) => srv !== id,
		);

		form.setFieldValue("structuredServices", services);
	});

	const handleDeleteFreeFormCategory = useEventCallback((id: string) => {
		const services = form.values.freeformServices ?? [];
		const filteredServices = services.filter((srv) => srv.name !== id);
		form.setFieldValue("freeformServices", filteredServices);
	});

	const options = useMemo(() => {
		return categories
			.filter((x) => !!x.name)
			.map((category) => ({
				label: category.displayName as string,
				value: category.name as string,
			}));
	}, [categories]);

	const handleNewService = useEventCallback(() => {
		dialog.open(DialogAddService, {
			selectedCategories,
			form,
		});
	});

	const getSelected = useCallback(
		(value: string | undefined) => {
			if (!value) {
				return undefined;
			}

			return options.find((x) => x.value === value);
		},
		[options],
	);

	return (
		<div>
			<ExtraForm.Group label="Primary Category">
				<VirtualizedSelectTmp
					options={options}
					value={getSelected(form.values.category)}
					onChange={(option) => form.setFieldValue("category", option?.value)}
				/>
			</ExtraForm.Group>
			{!!form.values.additionalCategories?.length && (
				<ExtraForm.Group label="Additional Categories">
					{form.values.additionalCategories?.map((category, idx) => (
						<div key={category} className={styles.additionalCategory}>
							<VirtualizedSelectTmp
								className="w-100"
								options={options}
								value={getSelected(form.values?.additionalCategories?.[idx])}
								onChange={(option) =>
									handleAdditionalCategoryChange(option?.value, idx)
								}
							/>
							<ButtonIcon
								className="remove"
								icon="delete"
								onClick={() => handleRemoveCategory(idx)}
							/>
						</div>
					))}
				</ExtraForm.Group>
			)}
			<Button
				onClick={handleAddCategory}
				className="mb-3"
				size="sm"
				disabled={!canAddNewCategory}
			>
				Add more categories
			</Button>

			<CategoryMappingPanel locationId={locationID} />

			<ExtraForm.Group label="Services">
				<Tag.List>
					{form.values.structuredServices?.map((service) => {
						const serviceDetails = allServices.find(
							(srv) => srv?.serviceTypeId === service,
						);

						return (
							<Tag
								key={service}
								name={
									serviceDetails?.displayName ?? "Could not load service name"
								}
								onRemove={() => handleRemoveStructuredService(service)}
							/>
						);
					})}
					{form.values.freeformServices?.map((service, idx) => {
						return (
							<Tag
								name={service.name ?? "could not load service name"}
								key={service.name ?? `${idx}-struct-service`}
								onRemove={() => handleDeleteFreeFormCategory(service.name)}
							/>
						);
					})}
					<Tag.IconButton icon="add" onClick={handleNewService} />
				</Tag.List>
			</ExtraForm.Group>
		</div>
	);
};

export default FormBusinessServicesProducts;
